import { useEffect, useMemo, useState } from 'react'
import {
  StudioPayload, Departure, CostModel, ELASTICITY, FitObjective, CarriageSet, ReturnMode, EngineOpts,
  FleetCaps, Network, EngineParams,
  mkSlots, baselineDeps, runScenario, costFromDeps, planSets, applySetPlan, legLocoCodes,
  gbp, capOf, setSeats, journeyMin, DEFAULT_NET, DEFAULT_FLEET,
} from './researchStudioModel'
import { buildSchedulePdf, SchedulePdfData } from './researchSchedulePdf'
import { ResearchTimetable } from './ResearchTimetable'
import { YearComparePanel, type YearRow } from './studio/YearComparePanel'
import type { DateActual } from './studio/dateActuals'
import { valueDepsOnDay, daySide, isNoStandardServiceDay, NO_SERVICE_LABEL, type DaySideFigures } from './studio/perDateValue'

// Weekly Service Schedule (Studio v4, Chris 2026-07-22).
//
// The modelled month laid out as calendar weeks (Mon → Sun): every date is
// modelled through the SAME passenger-day engine + cost model as the Studio
// above, using its day-class (Sat / Sun / Weekday). The currently selected
// day-class runs YOUR Studio scenario; the other day-classes run their
// observed baseline timetable. Each day shows services (steam/diesel),
// passengers, revenue (click for the modelled ticket split), costs and
// profit; weekly totals close each row and a monthly overview sums the month.
// A ticket-price panel re-prices the observed ticket mix (elasticity −0.3)
// and flows the result through demand AND capacity before it lands here.

const DAY_HEADS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const DOW_FULL = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
const GOLD = '#c89b3c'
const GREEN = '#2e7d32'
const RED = '#b23b2e'
const TEAL = '#0b3d2e'
const num0 = new Intl.NumberFormat('en-GB', { maximumFractionDigits: 0 })

const classOf = (col: number): string => (col === 5 ? 'Sat' : col === 6 ? 'Sun' : 'Wkdy')
const clsLabel: Record<string, string> = { Sat: 'Saturday', Sun: 'Sunday', Wkdy: 'Weekday' }
// A representative day-of-week per class, for class-level notes (any weekday works — Mon).
const CLS_DOW: Record<string, number> = { Wkdy: 1, Sat: 6, Sun: 7 }

interface MixRow { item: string; qty: number; net: number }

/** Aggregate detail rows by item. */
function addMix(into: Map<string, MixRow>, rows: { item: string; qty: number; net: number }[]): void {
  for (const r of rows) {
    const cur = into.get(r.item) ?? { item: r.item, qty: 0, net: 0 }
    cur.qty += r.qty; cur.net += r.net
    into.set(r.item, cur)
  }
}

/** Re-price a mix: each edited ticket's net scales by (p'/p)^(1+e), qty by (p'/p)^e. */
function repriceMix(rows: MixRow[], applied: Record<string, number>): MixRow[] {
  return rows.map((r) => {
    const p0 = r.qty > 0 ? r.net / r.qty : 0
    const p1 = applied[r.item]
    if (p1 == null || p0 <= 0) return r
    const ratio = p1 / p0
    return { item: r.item, qty: r.qty * Math.pow(ratio, ELASTICITY), net: r.net * Math.pow(ratio, 1 + ELASTICITY) }
  })
}

/** Demand/revenue multipliers a re-priced mix implies for one day-class. */
function classMults(rows: MixRow[], applied: Record<string, number>): { rev: number; pax: number } {
  const before = rows.reduce((t, r) => ({ qty: t.qty + r.qty, net: t.net + r.net }), { qty: 0, net: 0 })
  const after = repriceMix(rows, applied).reduce((t, r) => ({ qty: t.qty + r.qty, net: t.net + r.net }), { qty: 0, net: 0 })
  return {
    rev: before.net > 0 ? after.net / before.net : 1,
    pax: before.qty > 0 ? after.qty / before.qty : 1,
  }
}

export interface DayModel {
  cls: string; services: number; steamN: number; dieselN: number
  pax: number; revenue: number; costs: number; profit: number; daysRunObserved: number
  /** Rate-card split of `costs` and the day's train-miles — so a date can be
   *  re-priced on the accounts basis, or carried at fixed cost only. */
  dayFixed: number; miles: number
  /** Carriage sets in traffic that day (fixed rakes) + their codes. */
  sets: number; setCodes: string
  /** The day's set-applied timetable + per-leg set and loco (for the services drill-down and the WTT). */
  applied: Departure[]
  setBy: Record<string, string>
  locoBy: Record<string, { code: string; traction: 'steam' | 'diesel' | 'railcar'; label: string }>
}

interface ScheduleProps {
  data: StudioPayload
  monthKey: string
  dayClass: string
  deps: Departure[]
  /** Full-week mode: saved per-day-of-week timetables (Mon=1 … Sun=7). Days absent run `deps`. */
  dayOverrides: Record<number, Departure[]>
  /** Carriage set roster — the hard capacity constraint. */
  sets: CarriageSet[]
  objective: FitObjective
  returnMode: ReturnMode
  /** Passengers allowed to be left unreturned. Zero is a hard rule. */
  maxUnreturned: number
  engineOpts: EngineOpts
  tolerance: number
  recapture: number
  fareMult: number
  dwellMin: number
  rates: CostModel
  dieselLoco: number
  /** The live network — Mode B has to be scored on its own geography. */
  net?: Network
  fleet?: FleetCaps
  /**
   * The REAL working timetable (decoded from thebigplan, W0). When present the
   * Unoptimised side runs THIS — the same "current" the rest of the Studio
   * scores — rather than the sales-derived approximation, whose same-minute
   * slots merge real workings (the two 12:35s) and carry no set pins.
   */
  baselineTimetable?: Departure[]
  /** The season programme rows — powers the SEASON view toggle (Chris,
   *  2026-08-20: month summary AND a year view on this comparison panel). */
  yearRows?: YearRow[]
  seasonLabel?: string
  /** Rendered above the season table — the "design every month" action. */
  seasonAction?: React.ReactNode
  /**
   * The running calendar for this month, by ISO date: does the railway run
   * that day, and which timetable does it work. Drives the calendar's
   * per-DATE truth — the day models below are per day-of-week, so without
   * this a closed Friday still painted like a running one (Chris,
   * 2026-08-24).
   */
  dayCalendar?: Record<string, { running: boolean; colour?: string }>
  /** Rendered below the season table — the proposed-season day-by-day panel. */
  seasonExtra?: React.ReactNode
  /**
   * Which part a host shows (Timetable Studio's Modelling stage, Chris,
   * 2026-10-28): 'season' = the season roll-up (its own pill before the
   * months), 'month' = the month's schedule, 'timetable' = the month's
   * working timetable — each its own tab. Unset = the standalone panel with
   * its own Month/Season toggle and the timetable under the schedule.
   */
  part?: 'season' | 'month' | 'timetable'
  /** The year whose sales the month's demand rows carry, when it is not the
   *  calendar year on screen — a season still to trade takes its chosen
   *  source season's (ResearchStudio::scheduleSalesFor). Dates stay the month's. */
  salesYear?: number
  /** Which season's sales price the unoptimised side (law-6 basis) — stated on
   *  the grid so nobody has to guess where the numbers come from. */
  salesBasis?: string
  /** REAL per-day sales — design ISO date -> the like-for-like basis day's
   *  actuals (Chris, 2026-09-02: never the same class average on every
   *  Saturday). The unoptimised view shows them directly; the optimised view
   *  values the scenario timetable against each day's slotRows demand. */
  dateActuals?: Record<string, DateActual>
  /** Who is one-way on TODAY's timetable, per design date then per demand slot
   *  (banked with the month's plans) — so a date's cell values the design
   *  against the same people the build treated as one-way. */
  oneWayToday?: Record<string, Record<string, number>>
  /** Pullman dining trafficking through each date (toggle-filtered). */
  pullmanByDate?: Record<string, { services: number; covers: number; net: number }>
  /** The accounts (P&L) cost model when loaded — the SAME pricing the season
   *  table, board paper and workbook use, so the grid's costs agree with them.
   *  `dayFixedShareFor` = this month's fixed cost spread over its running days
   *  (charged whether or not trains run); `dayVariableFor` = the day's variable
   *  cost from its activity. Absent = the rate card. */
  dayFixedShareFor?: (side: 'mod' | 'cur') => number
  dayVariableFor?: (services: number, carriedPerDay: number, miles?: number | null) => number
}

/** thebigplan's pattern palette — matched to the Calendar stage so a colour
 *  means the same thing on both screens. */
const TT_HEX: Record<string, string> = {
  RED: '#B83A2B', YELLOW: '#A6771C', GOLD: '#A6771C', PINK: '#C25F94',
  BLUE: '#2B7A8C', GREEN: '#2E7D4F', PURPLE: '#6B4FA0', DINER: '#4E4459',
  GALA: '#8C5A1F', BLACK: '#333333',
}
const ttHex = (name: string): string => TT_HEX[name.trim().toUpperCase()] ?? '#6b6b5e'

/** Everything buildDayModels needs — pure, so the exec PDF can reuse it. */
export interface BuildDayModelsArgs {
  data: StudioPayload
  y: number
  m: number
  dayClass: string
  deps: Departure[]
  dayOverrides: Record<number, Departure[]>
  baselineTimetable?: Departure[]
  sets: CarriageSet[]
  objective: FitObjective
  maxUnreturned: number
  ep: EngineParams
  fareMult: number
  rates: CostModel
  dieselLoco: number
  net: Network
  fleet: FleetCaps
  /** Ticket re-pricing multipliers per day-class ({rev:1,pax:1} = untouched). */
  mults: Record<string, { rev: number; pax: number }>
  useScenario: boolean
}

/**
 * One modelled day per day OF WEEK (Mon=1 … Sun=7) — pure, no hooks, so the
 * exec PDF's month-view appendix can reuse it. OPTIMISED (useScenario) runs
 * the Studio design (per-day overrides in Full-week mode); UNOPTIMISED runs
 * the current timetable — the real WTT when supplied, else the sales-derived
 * approximation. Same sliders and ticket re-pricing both sides, so the
 * difference is purely the timetable.
 */
export function buildDayModels(a: BuildDayModelsArgs): Record<number, DayModel | null> {
  const { data, y, m, dayClass, deps, dayOverrides, sets, objective, maxUnreturned, ep, fareMult, rates, dieselLoco, net, fleet, mults, useScenario } = a
  const out: Record<number, DayModel | null> = {}
  const currentDeps = (north: ReturnType<typeof mkSlots>, south: ReturnType<typeof mkSlots>): Departure[] =>
    a.baselineTimetable && a.baselineTimetable.length > 0
      ? a.baselineTimetable.map((x) => ({ ...x }))
      : baselineDeps(net, north, south)
  // Mon–Fri share a timetable+demand unless individually overridden — plan once per distinct pair.
  const planCache = new Map<string, { applied: Departure[]; setsN: number; setCodes: string; carriages: number; setBy: Record<string, string>; locoBy: Record<string, { code: string; traction: 'steam' | 'diesel' | 'railcar'; label: string }> }>()
  for (let dow = 1; dow <= 7; dow++) {
    const cls = dow === 6 ? 'Sat' : dow === 7 ? 'Sun' : 'Wkdy'
    const rows = data.demand.filter((r) => r.yr === y && r.month_no === m && r.day_class === cls)
    if (rows.length === 0) { out[dow] = null; continue }
    const mult = mults[cls] ?? { rev: 1, pax: 1 }
    const yieldMult = mult.pax > 0 ? mult.rev / mult.pax : 1
    const scale = (s: ReturnType<typeof mkSlots>[number]) => ({ ...s, paxPerDay: s.paxPerDay * mult.pax, netPerPax: s.netPerPax * yieldMult })
    const north = mkSlots(net, rows, 'Northbound').map(scale)
    const south = mkSlots(net, rows, 'Southbound').map(scale)
    const ovr = dayClass === 'Week' ? dayOverrides[dow] : undefined
    const d = !useScenario
      ? currentDeps(north, south)
      : dayClass === 'Week'
        ? (ovr ?? deps)
        : cls === dayClass ? deps : currentDeps(north, south)
    // Carriage-set plan: every leg's capacity = its assigned fixed rake.
    const src = !useScenario ? 'base' : ovr ? `ovr${dow}` : dayClass === 'Week' || cls === dayClass ? 'scen' : 'base2'
    const cacheKey = `${cls}|${src}`
    let planned = planCache.get(cacheKey)
    if (!planned) {
      const plan = planSets(net, [{ cls, weight: 1, north, south }], d, sets, ep, objective, rates, dieselLoco, fleet, maxUnreturned)
      const applied = applySetPlan(d, plan)
      planned = {
        applied,
        setsN: plan.chains.filter((c) => c.set).length,
        setCodes: plan.chains.filter((c) => c.set).map((c) => c.set!.code).join(' '),
        carriages: plan.carriages,
        setBy: plan.byDep,
        locoBy: legLocoCodes(net, applied, dieselLoco),
      }
      planCache.set(cacheKey, planned)
    }
    const sc = runScenario(net, north, south, planned.applied, ep)
    const costs = costFromDeps(net, planned.applied, rates, dieselLoco, fleet, planned.carriages, planned.setsN)
    const un = data.untimed.find((u) => u.yr === y && u.month_no === m && u.day_class === cls)
    const untimedNet = un && un.running_days > 0 ? (un.net / un.running_days) * mult.rev : 0
    const untimedPax = un && un.running_days > 0 ? (un.pax / un.running_days) * mult.pax : 0
    const revenue = sc.revenue + untimedNet * fareMult
    out[dow] = {
      cls,
      services: planned.applied.filter((x) => x.enabled).length,
      steamN: costs.steamN,
      dieselN: costs.dieselN + costs.railcars,
      pax: sc.carried + untimedPax,
      revenue,
      costs: costs.total,
      profit: revenue - costs.total,
      dayFixed: costs.dayFixed,
      miles: costs.steamMiles + costs.dieselMiles,
      daysRunObserved: un?.running_days ?? Math.max(0, ...rows.map((r) => r.days_run)),
      sets: planned.setsN,
      setCodes: planned.setCodes,
      applied: planned.applied,
      setBy: planned.setBy,
      locoBy: planned.locoBy,
    }
  }
  return out
}

export function ResearchSchedule(props: ScheduleProps) {
  const { data, monthKey, dayClass, deps, dayOverrides, sets, objective, returnMode, maxUnreturned, engineOpts, tolerance, recapture, fareMult, dwellMin, rates, dieselLoco } = props
  const net = props.net ?? DEFAULT_NET
  const fleet = props.fleet ?? DEFAULT_FLEET
  const STATIONS = net.stations.map((s) => s.name)
  const ep: EngineParams = { toleranceMin: tolerance, recapture, fareMult, dwellMin, returnMode, opts: engineOpts }
  const [y, m] = monthKey.split('-').map(Number)
  const monthLabel = `${MONTHS[m - 1]} ${y}`

  // --- Observed ticket mix for the month, per day-class ---------------------
  const [mix, setMix] = useState<Record<string, MixRow[]> | null>(null)
  const [mixError, setMixError] = useState<string | null>(null)
  useEffect(() => {
    let live = true
    setMix(null); setMixError(null)
    // The detail endpoint returns the ticket items nested under `cur.items`
    // (alongside `routes`, `services`, and a year-ago `prev` basis) since the
    // 2026-07 research-tab rework — NOT a flat top-level `rows`, which is what
    // this loader used to read. Reading the old shape gave `undefined`, and the
    // subsequent `for..of` threw "t is not iterable", so the whole mix failed
    // and the schedule silently dropped to unadjusted demand. Read `cur.items`,
    // and treat a missing basis as an empty mix rather than throwing.
    Promise.all([1, 2, 3, 4, 5, 6, 7].map((d) =>
      fetch(`/api/research/weekday-revenue/detail?people=1&day_no=${d}&y=${y}&m=${m}`)
        .then(async (r) => { if (!r.ok) { const b = await r.json().catch(() => ({})); throw new Error((b as { error?: string }).error || `${r.status}`) } return r.json() as Promise<{ cur?: { items?: MixRow[] } }> })
        .then((b) => ({ dayNo: d, rows: b.cur?.items ?? [] }))
    ))
      .then((all) => {
        if (!live) return
        const byClass: Record<string, Map<string, MixRow>> = { Sat: new Map(), Sun: new Map(), Wkdy: new Map(), all: new Map() }
        for (const { dayNo, rows } of all) {
          const cls = dayNo === 6 ? 'Sat' : dayNo === 7 ? 'Sun' : 'Wkdy'
          addMix(byClass[cls], rows)
          addMix(byClass.all, rows)
        }
        setMix(Object.fromEntries(Object.entries(byClass).map(([k, v]) => [k, [...v.values()].sort((a, b) => b.net - a.net)])))
      })
      .catch((e) => { if (live) setMixError(String((e as Error)?.message ?? e)) })
    return () => { live = false }
  }, [y, m])

  // --- Ticket price panel ----------------------------------------------------
  const priceRows = useMemo(() => (mix?.all ?? []).filter((r) => r.qty > 0).slice(0, 14), [mix])
  const [priceInputs, setPriceInputs] = useState<Record<string, string>>({})
  const [applied, setApplied] = useState<Record<string, number>>({})
  useEffect(() => { setPriceInputs({}); setApplied({}) }, [monthKey])

  const applyPrices = () => {
    const next: Record<string, number> = {}
    for (const r of priceRows) {
      const raw = priceInputs[r.item]
      if (raw == null || raw.trim() === '') continue
      const p1 = Number(raw)
      const p0 = r.net / r.qty
      if (Number.isFinite(p1) && p1 > 0 && Math.abs(p1 - p0) > 0.004) next[r.item] = p1
    }
    setApplied(next)
  }

  const mults = useMemo(() => {
    const out: Record<string, { rev: number; pax: number }> = {}
    for (const cls of ['Sat', 'Sun', 'Wkdy']) out[cls] = mix ? classMults(mix[cls] ?? [], applied) : { rev: 1, pax: 1 }
    return out
  }, [mix, applied])

  // --- One modelled day per day OF WEEK (Mon=1 … Sun=7) -----------------------
  // Two sets: OPTIMISED (the Studio scenario — in Full-week mode each day runs
  // its saved override, else the shared weekly timetable) and UNOPTIMISED
  // (every day runs its observed baseline timetable). Same sliders and price
  // adjustments for both, so the difference is purely the timetable.
  const scenarioModels = useMemo(
    () => buildDayModels({ data, y: props.salesYear ?? y, m, dayClass, deps, dayOverrides, baselineTimetable: props.baselineTimetable, sets, objective, maxUnreturned, ep, fareMult, rates, dieselLoco, net, fleet, mults, useScenario: true }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, y, m, dayClass, deps, dayOverrides, props.baselineTimetable, sets, objective, returnMode, maxUnreturned, engineOpts, tolerance, recapture, fareMult, dwellMin, rates, dieselLoco, mults]
  )
  const baselineModels = useMemo(
    () => buildDayModels({ data, y: props.salesYear ?? y, m, dayClass, deps, dayOverrides, baselineTimetable: props.baselineTimetable, sets, objective, maxUnreturned, ep, fareMult, rates, dieselLoco, net, fleet, mults, useScenario: false }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, y, m, props.baselineTimetable, sets, objective, returnMode, maxUnreturned, engineOpts, tolerance, recapture, fareMult, dwellMin, rates, dieselLoco, mults]
  )
  const [view, setView] = useState<'optimised' | 'unoptimised'>('optimised')
  // Month = the calendar below; Season = the whole modelling period, month by
  // month, current vs modelled with grand totals.
  const [spanPick, setSpan] = useState<'month' | 'season'>('month')
  const span: 'month' | 'season' = props.part ? (props.part === 'season' ? 'season' : 'month') : spanPick
  const dayModels = view === 'optimised' ? scenarioModels : baselineModels

  // Day-for-day OPTIMISED valuation (Chris, 2026-09-02: "your modelled season
  // uses the SALES as they were — like for like, day for day"): each date's
  // cell values the scenario timetable against its like-for-like basis day's
  // ACTUAL slot sales — never the day-class average.
  const optDateVals = useMemo(() => {
    const out: Record<string, { pax: number; revenue: number }> = {}
    if (!props.dateActuals) return out
    for (const [iso, act] of Object.entries(props.dateActuals)) {
      // A like day that ran no standard services is zeroed by daySide — never
      // valued (its untimed till admissions are event traffic, not demand).
      if (isNoStandardServiceDay(act)) continue
      if (!act.slotRows || (act.slotRows.length === 0 && !act.untimedNet)) continue
      const dow0 = new Date(`${iso}T00:00:00Z`).getUTCDay()
      const md = scenarioModels[dow0 === 0 ? 7 : dow0]
      if (!md) continue
      out[iso] = valueDepsOnDay(net, md.applied, act, ep, fareMult, props.oneWayToday?.[iso])
    }
    return out
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.dateActuals, props.oneWayToday, scenarioModels, fareMult, tolerance, recapture, dwellMin, returnMode, engineOpts])

  /** A date-cell's figures for the given view — THE shared per-date rule
   *  (perDateValue.ts::daySide, the same call the season table, board paper
   *  and workbook make): unoptimised = the like day's real sales; optimised =
   *  the timetable valued on that day's real demand; a like day that ran no
   *  standard services = 0 services, fixed cost only; no like day = the
   *  day-of-week model, labelled by no tag. Costs come from the accounts model
   *  when loaded (dayCostFor), else the rate card. */
  const cellVals = (iso: string, md: DayModel, v: 'optimised' | 'unoptimised'): DaySideFigures & { tag: string | null } => {
    const a = props.dateActuals?.[iso] ?? null
    const side = v === 'optimised' ? 'mod' : 'cur'
    const cost = (pax: number, noService: boolean): { fixed: number; variable: number } => {
      const runs = !noService && md.services > 0
      if (props.dayFixedShareFor && props.dayVariableFor) return { fixed: props.dayFixedShareFor(side), variable: runs ? props.dayVariableFor(md.services, pax, md.miles) : 0 }
      return { fixed: md.dayFixed, variable: runs ? Math.max(0, md.costs - md.dayFixed) : 0 }
    }
    const valued = v === 'unoptimised' ? (a ? { pax: a.pax, revenue: a.net } : null) : (optDateVals[iso] ?? null)
    const f = daySide({
      act: a, plan: { services: md.services, sets: md.sets, steamN: md.steamN, dieselN: md.dieselN, miles: md.miles },
      valued, valuedKind: v === 'unoptimised' ? 'actual' : 'valued', average: { pax: md.pax, revenue: md.revenue }, cost,
    })
    const tag = f.kind === 'no-service' ? `${NO_SERVICE_LABEL} · like day ${a?.basisDate ?? ''}`.trim()
      : f.kind === 'actual' ? `actual · ${a?.basisDate ?? ''}`
      : f.kind === 'valued' ? `demand of ${a?.basisDate ?? 'like day'}`
      : null
    return { ...f, tag }
  }

  // --- Calendar layout (Mon → Sun weeks) --------------------------------------
  const weeks = useMemo(() => {
    const daysInMonth = new Date(y, m, 0).getDate()
    const firstCol = (new Date(y, m - 1, 1).getDay() + 6) % 7 // Mon=0 … Sun=6
    const out: (number | null)[][] = []
    let row: (number | null)[] = Array(firstCol).fill(null)
    for (let d = 1; d <= daysInMonth; d++) {
      row.push(d)
      if (row.length === 7) { out.push(row); row = [] }
    }
    if (row.length > 0) out.push([...row, ...Array(7 - row.length).fill(null)])
    return out
  }, [y, m])

  // Totals honour (a) the running calendar — a non-running Tuesday adds
  // nothing (it used to be counted at the day-of-week average: an overcount) —
  // and (b) the per-DATE values of the given view (cellVals: actuals on the
  // unoptimised side, day-for-day demand valuation on the optimised side).
  const weekTotal = (row: (number | null)[], models: Record<number, DayModel | null>, v: 'optimised' | 'unoptimised') => {
    const t = { services: 0, pax: 0, revenue: 0, costs: 0, profit: 0, days: 0, sets: 0 }
    row.forEach((d, col) => {
      if (d == null) return
      const iso = `${monthKey}-${String(d).padStart(2, '0')}`
      const cal = props.dayCalendar?.[iso]
      if (cal && !cal.running) return // the calendar owns whether a DATE runs
      const md = models[col + 1]
      if (!md) return
      const cv = cellVals(iso, md, v)
      t.services += cv.services; t.pax += cv.pax; t.revenue += cv.revenue; t.costs += cv.costs; t.profit += cv.profit; t.days += 1
      t.sets = Math.max(t.sets, cv.sets)
    })
    return t
  }
  const sumMonth = (models: Record<number, DayModel | null>, v: 'optimised' | 'unoptimised') => {
    const t = { services: 0, pax: 0, revenue: 0, costs: 0, profit: 0, days: 0, sets: 0 }
    for (const row of weeks) {
      const w = weekTotal(row, models, v)
      t.services += w.services; t.pax += w.pax; t.revenue += w.revenue; t.costs += w.costs; t.profit += w.profit; t.days += w.days
      t.sets = Math.max(t.sets, w.sets)
    }
    return t
  }
  const monthTotalOpt = useMemo(
    () => sumMonth(scenarioModels, 'optimised'),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [weeks, scenarioModels, props.dayCalendar, optDateVals]
  )
  const monthTotalUnopt = useMemo(
    () => sumMonth(baselineModels, 'unoptimised'),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [weeks, baselineModels, props.dayCalendar, props.dateActuals]
  )

  const classDayCounts = useMemo(() => {
    const c: Record<string, number> = { Sat: 0, Sun: 0, Wkdy: 0 }
    for (const row of weeks) row.forEach((d, col) => { if (d != null && dayModels[col + 1]) c[classOf(col)] += 1 })
    return c
  }, [weeks, dayModels])
  const dowDayCounts = useMemo(() => {
    const c: Record<number, number> = {}
    for (const row of weeks) row.forEach((d, col) => { if (d != null && dayModels[col + 1]) c[col + 1] = (c[col + 1] ?? 0) + 1 })
    return c
  }, [weeks, dayModels])

  // --- Running-services drill-down modal -----------------------------------------
  const [svc, setSvc] = useState<{ label: string; model: DayModel } | null>(null)
  const fmtMin = (mins: number) => `${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(Math.round(mins) % 60).padStart(2, '0')}`

  // --- Modelled ticket split modal ---------------------------------------------
  const [split, setSplit] = useState<{ cls: string; label: string; pax: number; revenue: number } | null>(null)
  const splitRows = useMemo(() => {
    if (!split || !mix) return null
    const rows = repriceMix(mix[split.cls] ?? [], applied)
    const tot = rows.reduce((t, r) => ({ qty: t.qty + r.qty, net: t.net + r.net }), { qty: 0, net: 0 })
    if (tot.net <= 0) return []
    return rows
      .filter((r) => r.net > 0)
      .sort((a, b) => b.net - a.net)
      .slice(0, 25)
      .map((r) => ({
        item: r.item,
        tickets: tot.qty > 0 ? (r.qty / tot.qty) * split.pax : 0,
        net: (r.net / tot.net) * split.revenue,
        share: r.net / tot.net,
      }))
  }, [split, mix, applied])

  // --- PDF -------------------------------------------------------------------
  const downloadPdf = () => {
    const pdf: SchedulePdfData = {
      season: data.season,
      monthLabel,
      dayClassScenario: dayClass === 'Week' ? 'Full week' : clsLabel[dayClass],
      assumptions: [
        `Tolerance ±${tolerance}m · recapture ${Math.round(recapture * 100)}% · time at destination ≥ ${Math.floor(dwellMin / 60)}h${dwellMin % 60 ? ` ${dwellMin % 60}m` : ''} · fares ${Math.round(fareMult * 100)}%`,
        `Rates: full cost model (${rates.locos.length} loco classes, per-mile) · ${dieselLoco} loco diagram${dieselLoco === 1 ? '' : 's'} designated diesel`,
        `Carriage set roster (fixed rakes): ${sets.filter((s) => s.active).map((s) => `${s.code} ${setSeats(s)} seats @ ${s.start === 0 ? 'Pickering' : 'Grosmont'}`).join(' · ') || 'none active'} — capacity comes only from the assigned set's measured seats; swaps at Pickering/Grosmont stables.`,
        dayClass === 'Week'
          ? `All days use the Full-week scenario timetable${Object.keys(dayOverrides).length > 0 ? ` — customised days with their own saved timetable: ${Object.keys(dayOverrides).map(Number).sort().map((d) => DOW_FULL[d - 1]).join(', ')}` : ''}.`
          : `${clsLabel[dayClass]}s use the current Studio scenario; other day types run their observed baseline timetable.`,
        `Calendar shows the ${view === 'optimised' ? 'OPTIMISED (Studio scenario)' : 'UNOPTIMISED (observed baseline)'} timetable; the monthly overview compares both.`,
        ...(Object.keys(applied).length > 0
          ? [`Ticket prices adjusted: ${Object.entries(applied).map(([item, p1]) => {
              const r = priceRows.find((x) => x.item === item)
              return `${item} £${(r ? r.net / r.qty : 0).toFixed(2)} → £${p1.toFixed(2)}`
            }).join('; ')} (elasticity ${ELASTICITY})`]
          : ['Ticket prices as observed (no adjustments).']),
      ],
      weeks: weeks.map((row) => row.map((d, col) => {
        if (d == null) return null
        const md = dayModels[col + 1]
        return { date: d, model: md }
      })),
      weekTotals: weeks.map((row) => weekTotal(row, dayModels, view)),
      viewLabel: view === 'optimised' ? 'optimised (Studio scenario)' : 'unoptimised (observed baseline)',
      monthTotalOptimised: monthTotalOpt,
      monthTotalUnoptimised: monthTotalUnopt,
      classSummaries: [1, 2, 3, 4, 5, 6, 7]
        .filter((dow) => dayModels[dow])
        .map((dow) => ({ label: `${DOW_FULL[dow - 1]}s`, days: dowDayCounts[dow] ?? 0, model: dayModels[dow]! })),
      classMixes: (['Sat', 'Sun', 'Wkdy'] as const)
        .filter((cls) => mix && [1, 2, 3, 4, 5, 6, 7].some((dow) => classOf(dow - 1) === cls && dayModels[dow]))
        .map((cls) => {
          const dows = [1, 2, 3, 4, 5, 6, 7].filter((dow) => classOf(dow - 1) === cls && dayModels[dow])
          const avgPax = dows.reduce((t, dow) => t + dayModels[dow]!.pax, 0) / dows.length
          const avgRev = dows.reduce((t, dow) => t + dayModels[dow]!.revenue, 0) / dows.length
          const rows = repriceMix(mix![cls] ?? [], applied)
          const tot = rows.reduce((t, r) => ({ qty: t.qty + r.qty, net: t.net + r.net }), { qty: 0, net: 0 })
          return {
            label: clsLabel[cls],
            rows: rows.filter((r) => r.net > 0).slice(0, 10).map((r) => ({
              item: r.item,
              tickets: tot.qty > 0 ? (r.qty / tot.qty) * avgPax : 0,
              net: tot.net > 0 ? (r.net / tot.net) * avgRev : 0,
              share: tot.net > 0 ? r.net / tot.net : 0,
            })),
          }
        }),
    }
    buildSchedulePdf(pdf)
  }

  const cellStat = (label: string, value: string, color?: string, onClick?: () => void) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 4 }}>
      <span style={{ color: '#8a8a8a' }}>{label}</span>
      {onClick ? (
        <button
          type="button"
          onClick={onClick}
          title="Modelled ticket split"
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: color ?? TEAL, fontWeight: 600, textDecoration: 'underline dotted', fontSize: 'inherit' }}
        >{value}</button>
      ) : (
        <span style={{ color: color ?? '#1d1d1b', fontWeight: 600 }}>{value}</span>
      )}
    </div>
  )

  return (
    <div style={{ marginTop: 12, border: `1px solid ${TEAL}`, borderRadius: 8, padding: '10px 14px', background: '#fff' }}>
      {props.part !== 'season' && <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8 }}>
        <div>
          <div style={{ fontSize: '0.82rem', fontWeight: 700, color: TEAL }}>
            {props.part === 'timetable' ? `Timetable — ${monthLabel}` : `Weekly Service Schedule — ${monthLabel}`}
          </div>
          <div style={{ fontSize: '0.72rem', color: '#8a8a8a', maxWidth: 760 }}>
            Every day modelled through the passenger-day engine + cost model by its day type.
            {' '}{dayClass === 'Week'
              ? <strong>All days use your Full-week scenario timetable above{Object.keys(dayOverrides).length > 0 ? ` (customised: ${Object.keys(dayOverrides).map(Number).sort().map((d) => DAY_HEADS[d - 1]).join(', ')})` : ''}.</strong>
              : <><strong>{clsLabel[dayClass]}s use your Studio scenario above</strong>; other day types run their observed baseline timetable.</>}
            {' '}Click a day&apos;s revenue for its modelled ticket split.
          </div>
          {span === 'month' && (
            <div style={{ fontSize: '0.74rem', fontWeight: 700, color: '#8a6d1f', marginTop: 2 }}>
              {props.dateActuals && Object.keys(props.dateActuals).length > 0
                ? view === 'unoptimised'
                  ? `Unoptimised data source: ${props.salesBasis ?? 'sales basis unavailable'}. Passengers and takings are that season’s ACTUAL sales for each like-for-like day; services are the real timetable (which genuinely repeats per pattern).`
                  : `Optimised: the designed timetable valued DAY FOR DAY against ${props.salesBasis ?? 'the sales basis season'} — each date carries what the design would take of its like-for-like day’s actual demand.`
                : 'Per-day actual sales are unavailable for this month — figures are the day-type average, and say so.'}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {props.yearRows && !props.part && (
            <span style={{ display: 'inline-flex', border: `1px solid ${GOLD}`, borderRadius: 999, overflow: 'hidden' }} title="Month = this month's calendar. Season = every month of the modelling period, current vs modelled, with the grand totals.">
              {([['month', 'Month'], ['season', 'Season']] as const).map(([key, label]) => (
                <button key={key} type="button" onClick={() => setSpan(key)}
                  style={{ border: 'none', padding: '4px 12px', cursor: 'pointer', fontSize: '0.76rem', fontWeight: 600, background: span === key ? GOLD : '#fff', color: span === key ? '#1d1d1b' : '#8a6d1f' }}
                >{label}</button>
              ))}
            </span>
          )}
          {span === 'month' && <span style={{ display: 'inline-flex', border: `1px solid ${TEAL}`, borderRadius: 999, overflow: 'hidden' }} title={`Optimised = your Studio scenario timetable. Unoptimised = ${props.baselineTimetable && props.baselineTimetable.length > 0 ? 'the REAL working timetable (thebigplan)' : 'the observed sales-derived timetable'}, same sliders and price adjustments — the difference is purely the timetable.`}>
            {([['optimised', 'Optimised'], ['unoptimised', 'Unoptimised']] as const).map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setView(key)}
                style={{
                  border: 'none', padding: '4px 12px', cursor: 'pointer', fontSize: '0.76rem', fontWeight: 600,
                  background: view === key ? TEAL : '#fff', color: view === key ? '#fff' : TEAL,
                }}
              >{label}</button>
            ))}
          </span>}
          {span === 'month' && <button
            type="button"
            onClick={downloadPdf}
            disabled={!mix}
            style={{ background: TEAL, color: '#fff', border: 'none', borderRadius: 6, padding: '5px 14px', cursor: mix ? 'pointer' : 'default', fontWeight: 700, opacity: mix ? 1 : 0.5 }}
          >Download PDF</button>}
        </div>
      </div>}

      {/* SEASON view — the whole modelling period, current vs modelled. */}
      {span === 'season' && props.yearRows && (
        <div style={{ marginTop: 10 }}>
          {props.seasonAction}
          <YearComparePanel rows={props.yearRows} seasonLabel={props.seasonLabel ?? ''} />
          {props.seasonExtra}
        </div>
      )}

      {span === 'month' && <>
      {props.part !== 'timetable' && <>
      {mixError && <p style={{ color: '#a33', fontSize: '0.8rem' }}>Ticket mix failed to load: {mixError} — schedule uses unadjusted demand.</p>}

      {/* Ticket price panel */}
      <div style={{ margin: '10px 0', border: '1px solid #e5e1d6', borderRadius: 8, padding: '8px 12px', background: '#faf9f5' }}>
        <div style={{ fontSize: '0.76rem', fontWeight: 700, color: TEAL, marginBottom: 4 }}>
          Adjust ticket prices <span style={{ fontWeight: 400, color: '#8a8a8a' }}>— observed {monthLabel} mix; each change flows through demand (elasticity {ELASTICITY}) and train capacity before the schedule below</span>
        </div>
        {!mix && !mixError && <p style={{ fontSize: '0.78rem', color: '#6b6b6b', margin: 0 }}>Loading ticket mix…</p>}
        {mix && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '3px 16px', fontSize: '0.76rem' }}>
              {priceRows.map((r) => {
                const p0 = r.net / r.qty
                return (
                  <label key={r.item} style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'space-between' }}>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={`${r.item} — avg £${p0.toFixed(2)}, ${num0.format(r.qty)} tickets in ${monthLabel}`}>{r.item}</span>
                    <span style={{ whiteSpace: 'nowrap', color: '#8a8a8a' }}>
                      £{p0.toFixed(2)} →{' '}
                      <input
                        type="number" min={0} step={0.5} placeholder={p0.toFixed(2)}
                        value={priceInputs[r.item] ?? ''}
                        onChange={(e) => setPriceInputs((prev) => ({ ...prev, [r.item]: e.target.value }))}
                        style={{ width: 64 }}
                      />
                    </span>
                  </label>
                )
              })}
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6, fontSize: '0.76rem' }}>
              <button type="button" onClick={applyPrices} style={{ background: GOLD, color: '#1d1d1b', fontWeight: 700, border: 'none', borderRadius: 6, padding: '3px 14px', cursor: 'pointer' }}>Apply</button>
              <button type="button" onClick={() => { setPriceInputs({}); setApplied({}) }} style={{ background: '#eee', border: 'none', borderRadius: 6, padding: '3px 10px', cursor: 'pointer' }}>Reset</button>
              {Object.keys(applied).length > 0 ? (
                <span style={{ color: '#6b6b6b' }}>
                  {Object.keys(applied).length} price{Object.keys(applied).length === 1 ? '' : 's'} applied — revenue effect:{' '}
                  {(['Sat', 'Sun', 'Wkdy'] as const).filter((c) => dayModels[CLS_DOW[c]]).map((c) => `${c} ×${mults[c].rev.toFixed(3)}`).join(' · ')}
                </span>
              ) : (
                <span style={{ color: '#8a8a8a' }}>No adjustments applied.</span>
              )}
            </div>
          </>
        )}
      </div>

      {/* Calendar grid */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '0.72rem', minWidth: 940 }}>
          <thead>
            <tr>
              {DAY_HEADS.map((h) => (
                <th key={h} style={{ border: '1px solid #e5e1d6', background: TEAL, color: '#fff', padding: '4px 6px', fontSize: '0.72rem' }}>{h}</th>
              ))}
              <th style={{ border: '1px solid #e5e1d6', background: '#1f4d3f', color: '#fff', padding: '4px 6px', fontSize: '0.72rem' }}>Week total</th>
            </tr>
          </thead>
          <tbody>
            {weeks.map((row, wi) => {
              const wt = weekTotal(row, dayModels, view)
              return (
                <tr key={wi}>
                  {row.map((d, col) => {
                    if (d == null) return <td key={col} style={{ border: '1px solid #f0ece1', background: '#faf9f5' }} />
                    const cls = classOf(col)
                    const iso = `${monthKey}-${String(d).padStart(2, '0')}`
                    const cal = props.dayCalendar?.[iso]
                    // The calendar is the authority on whether a DATE runs;
                    // the day models are per day-of-week.
                    const runs = cal ? cal.running : true
                    const md = runs ? dayModels[col + 1] : undefined
                    const colour = cal?.colour ?? ''
                    return (
                      <td key={col} style={{
                        border: runs ? '1px solid #e5e1d6' : '1px dashed #cfc8b8',
                        verticalAlign: 'top', padding: '4px 6px', minWidth: 116,
                        background: !runs ? '#f7f5ee' : md ? '#fff' : '#f5f3ec',
                        borderLeft: runs && colour ? `3px solid ${ttHex(colour)}` : undefined,
                      }}>
                        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 4, marginBottom: 2 }}>
                          <span style={{ fontWeight: 700, color: runs ? TEAL : '#b0a89a' }}>{d}</span>
                          {runs && colour && (
                            <span title={`${colour} timetable`} style={{ fontSize: '0.62rem', fontWeight: 700, color: ttHex(colour), textTransform: 'uppercase', letterSpacing: '.03em' }}>{colour}</span>
                          )}
                        </div>
                        {!runs && <div style={{ color: '#b0a89a' }}>not running</div>}
                        {runs && !md && <div style={{ color: '#b0a89a' }}>no service</div>}
                        {md && (() => {
                          // Each DATE its own value (Chris, 2026-09-02):
                          // unoptimised = the day's real sales; optimised = the
                          // scenario timetable valued on that day's real demand.
                          const cv = cellVals(iso, md, view)
                          const profit = cv.profit
                          const pm = props.pullmanByDate?.[iso]
                          if (cv.kind === 'no-service') {
                            // The like-for-like day ran no standard services (a
                            // gala, an event, or not running): 0 services, fixed
                            // cost only — said in words (Chris, 2026-09-03).
                            return (
                              <>
                                <div style={{ fontWeight: 700, color: '#8a8478', fontSize: '0.72rem' }}>No standard services</div>
                                <div style={{ fontSize: '0.6rem', color: '#8a6d1f' }} title="The like-for-like basis day sold no timed departure: the railway ran a gala or event, or did not run. Both sides carry 0 services, £0 and the fixed cost only.">{cv.tag}</div>
                                {cellStat('Fixed cost', gbp(cv.costs))}
                                {cellStat('Profit', `${profit >= 0 ? '+' : ''}${gbp(profit)}`, profit >= 0 ? GREEN : RED)}
                                {pm && <div style={{ fontSize: '0.6rem', color: '#6B4FA0', fontWeight: 700 }} title={`${pm.services} Pullman dining service(s) traffic through this day — ${num0.format(pm.covers)} covers, ${gbp(pm.net)} (outside the daily-services P&L)`}>Pullman ×{pm.services} · {num0.format(pm.covers)} covers</div>}
                              </>
                            )
                          }
                          return (
                            <>
                              {cellStat('Services', `${cv.services} (${md.steamN}S/${md.dieselN}D)`, TEAL, () => setSvc({ label: `${DAY_HEADS[col]} ${d} ${monthLabel}`, model: md }))}
                              {cellStat('Sets', `${md.sets}${md.setCodes ? ` — ${md.setCodes}` : ''}`)}
                              {cellStat('Passengers', `${num0.format(Math.round(cv.pax))}${cv.tag ? ' ✓' : ''}`)}
                              {cellStat('Revenue', gbp(cv.revenue), TEAL, () => setSplit({ cls, label: `${DAY_HEADS[col]} ${d} ${monthLabel}`, pax: cv.pax, revenue: cv.revenue }))}
                              {cellStat('Costs', gbp(cv.costs))}
                              {cellStat('Profit', `${profit >= 0 ? '+' : ''}${gbp(profit)}`, profit >= 0 ? GREEN : RED)}
                              {cv.tag && <div style={{ fontSize: '0.6rem', color: '#8a6d1f' }} title={view === 'unoptimised' ? 'The day’s actual sales' : 'The designed timetable valued against this day’s actual demand'}>{cv.tag}</div>}
                              {pm && <div style={{ fontSize: '0.6rem', color: '#6B4FA0', fontWeight: 700 }} title={`${pm.services} Pullman dining service(s) traffic through this day — ${num0.format(pm.covers)} covers, ${gbp(pm.net)} (outside the daily-services P&L)`}>Pullman ×{pm.services} · {num0.format(pm.covers)} covers</div>}
                            </>
                          )
                        })()}
                      </td>
                    )
                  })}
                  <td style={{ border: '1px solid #e5e1d6', verticalAlign: 'top', padding: '4px 6px', minWidth: 120, background: '#f6f8f6' }}>
                    <div style={{ fontWeight: 700, color: TEAL, marginBottom: 2 }}>Week {wi + 1}</div>
                    {cellStat('Services', num0.format(wt.services))}
                    {cellStat('Sets (peak)', num0.format(wt.sets))}
                    {cellStat('Passengers', num0.format(Math.round(wt.pax)))}
                    {cellStat('Revenue', gbp(wt.revenue))}
                    {cellStat('Costs', gbp(wt.costs))}
                    {cellStat('Profit', `${wt.profit >= 0 ? '+' : ''}${gbp(wt.profit)}`, wt.profit >= 0 ? GREEN : RED)}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* Monthly overview — both rows always, colour-coded so the comparison
          reads at a glance: green is the proposed timetable, amber is what we
          run today. The wordy "Month (optimised) —" prefix was wrapping every
          card onto three lines, so the row carries the label instead. */}
      {([
        {
          title: 'Optimised — proposed timetable', total: monthTotalOpt, other: monthTotalUnopt,
          active: view === 'optimised', bg: '#f2f8f2', border: '#b9d6ba', tint: '#29562b',
        },
        {
          title: 'Unoptimised — what we run today', total: monthTotalUnopt, other: null,
          active: view === 'unoptimised', bg: '#fdf6e3', border: '#e8d9a0', tint: '#8a6d1a',
        },
      ] as const).map((rowDef) => (
        <div key={rowDef.title} style={{ marginTop: 10 }}>
        <div style={{ fontSize: '0.72rem', fontWeight: 700, color: rowDef.tint, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>
          {rowDef.title}{rowDef.active ? ' · shown in the calendar above' : ''}
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {[
            { label: 'Modelled days', value: num0.format(rowDef.total.days), sub: (['Sat', 'Sun', 'Wkdy'] as const).filter((c) => classDayCounts[c] > 0).map((c) => `${classDayCounts[c]} ${clsLabel[c]}${classDayCounts[c] === 1 ? '' : 's'}`).join(' · ') || 'none' },
            { label: 'Services', value: num0.format(rowDef.total.services), sub: 'train departures across the month' },
            { label: 'Sets (peak)', value: num0.format(rowDef.total.sets), sub: 'most carriage sets in traffic on one day' },
            { label: 'Passengers', value: num0.format(Math.round(rowDef.total.pax)), sub: 'incl. untimed walk-up' },
            {
              label: 'Revenue', value: gbp(rowDef.total.revenue),
              sub: rowDef.other ? `vs unoptimised: ${rowDef.total.revenue >= rowDef.other.revenue ? '+' : ''}${gbp(rowDef.total.revenue - rowDef.other.revenue)}` : 'modelled travel revenue',
            },
            { label: 'Costs', value: gbp(rowDef.total.costs), sub: 'engines + carriages + overhead' },
            {
              label: 'Profit', value: `${rowDef.total.profit >= 0 ? '+' : ''}${gbp(rowDef.total.profit)}`,
              sub: rowDef.other ? `vs unoptimised: ${rowDef.total.profit >= rowDef.other.profit ? '+' : ''}${gbp(rowDef.total.profit - rowDef.other.profit)}` : 'revenue less running costs',
              color: rowDef.total.profit >= 0 ? GREEN : RED,
            },
          ].map((c) => (
            <div
              key={c.label}
              style={{
                flex: '1 1 140px',
                border: `1px solid ${rowDef.border}`,
                borderWidth: rowDef.active ? 2 : 1,
                borderRadius: 8,
                padding: '8px 12px',
                background: rowDef.bg,
              }}
            >
              <div style={{ fontSize: '0.7rem', color: rowDef.tint }}>{c.label}</div>
              <div style={{ fontSize: '1.1rem', fontWeight: 700, color: ('color' in c ? c.color : undefined) ?? TEAL }}>{c.value}</div>
              <div style={{ fontSize: '0.68rem', color: '#8a8a8a' }}>{c.sub}</div>
            </div>
          ))}
        </div>
        </div>
      ))}
      <div style={{ fontSize: '0.68rem', color: '#8a8a8a', marginTop: 6 }}>
        Assumes every day of a running day type operates.
        Observed running days: {(['Sat', 'Sun', 'Wkdy'] as const).filter((c) => dayModels[CLS_DOW[c]]).map((c) => `${clsLabel[c]} ${dayModels[CLS_DOW[c]]!.daysRunObserved}`).join(' · ') || '—'}.
      </div>

      </>}

      {/* Working timetable in thebigplan TTBuilder format + WTT Excel export */}
      {props.part !== 'month' && <ResearchTimetable
        models={dayModels}
        weeks={weeks}
        monthLabel={monthLabel}
        season={data.season}
        viewLabel={view === 'optimised' ? 'optimised scenario' : 'unoptimised baseline'}
      />}
      </>}

      {/* Running services drill-down */}
      {svc && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setSvc(null)}>
          <div style={{ background: '#fff', borderRadius: 10, padding: '16px 18px', width: 720, maxWidth: '94vw', maxHeight: '84vh', overflowY: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.25)' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
              <strong style={{ fontSize: '0.95rem' }}>Running services — {svc.label}</strong>
              <button type="button" onClick={() => setSvc(null)} style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: '#6b6b6b' }}>×</button>
            </div>
            <p style={{ fontSize: '0.72rem', color: '#8a8a8a', margin: '0 0 8px' }}>
              Arrival times derived from line journey times (Pickering–Levisham 25m · –Goathland 55m · –Grosmont 75m · –Whitby 105m).
              Capacity is the assigned carriage set&apos;s fixed rake.
            </p>
            {(['Northbound', 'Southbound'] as const).map((dir) => {
              const legs = svc.model.applied.filter((x) => x.enabled && x.direction === dir).sort((a, b) => a.minutes - b.minutes)
              return (
                <div key={dir} style={{ marginBottom: 10 }}>
                  <div style={{ fontSize: '0.8rem', fontWeight: 700, color: TEAL, marginBottom: 2 }}>{dir}{legs.length === 0 ? ' — no services' : ''}</div>
                  {legs.length > 0 && (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                      <thead>
                        <tr>
                          {['Depart', 'From', 'Arrive', 'To', 'Set', 'Capacity', 'Loco'].map((h) => (
                            <th key={h} style={{ textAlign: h === 'Set' || h === 'Capacity' || h === 'Loco' ? 'right' : 'left', padding: '3px 8px', borderBottom: '1px solid #e5e1d6', color: '#6b6b6b' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {legs.map((l) => {
                          const loco = svc.model.locoBy[l.id]
                          return (
                            <tr key={l.id}>
                              <td style={{ padding: '3px 8px', borderBottom: '1px solid #f0ece1', fontWeight: 700 }}>{l.time}</td>
                              <td style={{ padding: '3px 8px', borderBottom: '1px solid #f0ece1' }}>{STATIONS[l.o]}</td>
                              <td style={{ padding: '3px 8px', borderBottom: '1px solid #f0ece1' }}>{fmtMin(l.minutes + journeyMin(net, l.o, l.d))}</td>
                              <td style={{ padding: '3px 8px', borderBottom: '1px solid #f0ece1' }}>{STATIONS[l.d]}</td>
                              <td style={{ padding: '3px 8px', borderBottom: '1px solid #f0ece1', textAlign: 'right', fontWeight: 700, color: TEAL }}>{svc.model.setBy[l.id] ?? '—'}</td>
                              <td style={{ padding: '3px 8px', borderBottom: '1px solid #f0ece1', textAlign: 'right' }}>{l.capacity ?? capOf(l.coaches)}{l.coaches > 0 ? ` (${l.coaches} coaches)` : ' (Autocar)'}</td>
                              <td style={{ padding: '3px 8px', borderBottom: '1px solid #f0ece1', textAlign: 'right' }}>{loco ? `${loco.label} (${loco.code})` : '—'}</td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Modelled ticket split modal */}
      {split && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setSplit(null)}>
          <div style={{ background: '#fff', borderRadius: 10, padding: '16px 18px', width: 620, maxWidth: '94vw', maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.25)' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
              <strong style={{ fontSize: '0.95rem' }}>Modelled ticket split — {split.label}</strong>
              <button type="button" onClick={() => setSplit(null)} style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: '#6b6b6b' }}>×</button>
            </div>
            <p style={{ fontSize: '0.72rem', color: '#8a8a8a', margin: '0 0 8px' }}>
              Observed {clsLabel[split.cls]} ticket mix for {monthLabel}{Object.keys(applied).length > 0 ? ' (re-priced per your adjustments)' : ''}, scaled to this day&apos;s
              modelled {num0.format(Math.round(split.pax))} passengers and {gbp(split.revenue)} revenue — the same basis as the breakdowns on the weekday charts above.
            </p>
            {splitRows && splitRows.length === 0 && <p style={{ fontSize: '0.8rem', color: '#6b6b6b' }}>No ticket mix observed for this day type yet.</p>}
            {splitRows && splitRows.length > 0 && (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '4px 8px', borderBottom: '1px solid #e5e1d6', color: '#6b6b6b' }}>Ticket / item</th>
                    <th style={{ textAlign: 'right', padding: '4px 8px', borderBottom: '1px solid #e5e1d6', color: '#6b6b6b' }}>Modelled tickets</th>
                    <th style={{ textAlign: 'right', padding: '4px 8px', borderBottom: '1px solid #e5e1d6', color: '#6b6b6b' }}>Modelled net £</th>
                    <th style={{ textAlign: 'right', padding: '4px 8px', borderBottom: '1px solid #e5e1d6', color: '#6b6b6b' }}>% of revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {splitRows.map((r) => (
                    <tr key={r.item}>
                      <td style={{ padding: '3px 8px', borderBottom: '1px solid #f0ece1' }}>{r.item}</td>
                      <td style={{ padding: '3px 8px', borderBottom: '1px solid #f0ece1', textAlign: 'right' }}>{r.tickets >= 0.5 ? num0.format(Math.round(r.tickets)) : '—'}</td>
                      <td style={{ padding: '3px 8px', borderBottom: '1px solid #f0ece1', textAlign: 'right' }}>{gbp(r.net)}</td>
                      <td style={{ padding: '3px 8px', borderBottom: '1px solid #f0ece1', textAlign: 'right' }}>{(r.share * 100).toFixed(1)}%</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td style={{ padding: '4px 8px', fontWeight: 700 }}>Total</td>
                    <td style={{ padding: '4px 8px', textAlign: 'right', fontWeight: 700 }}>{num0.format(Math.round(split.pax))}</td>
                    <td style={{ padding: '4px 8px', textAlign: 'right', fontWeight: 700 }}>{gbp(split.revenue)}</td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
